/* istanbul ignore file */
import { ThemeProvider, EmotionCacheProvider } from '@ori-ui/mui';
import { NOT_LOGGED_IN_USER_ID, UniversalTokenProvider, doDevelopmentLogin } from '@ori/auth';
import { parseClientAppConfig } from '@ori/client-app-config';
import { ServiceUrlsProvider } from '@ori/configuration-manager';
import { EnvironmentProvider } from '@ori/environment';
import { TranslationsProvider } from '@ori/i18n';
import { LoggerProvider, ApplicationInsights, getClientApplicationInsightsConfig, ErrorBoundary } from '@ori/logger';
import { NotificationsMenu, defaultTranslationsWithNamespaces } from '@ori/notification-menu';
import { Team, TestIdProvider } from '@ori/testid-generator';
import { createRoot } from 'react-dom/client';
import { DevelopmentEnvironmentWrapper } from './DevelopmentEnvironmentProvider';
import { APP_NAME, EMOTION_CACHE_KEY } from './constants-generated';
import { mainLogger } from './logger';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = mainLogger.createChild('appRender');

/**
 * React app renderer that is used when app is used as a standalone app
 */
export async function render(rootElement) {
  // Notification menu has some old configuration object so we have to modify it a little bit
  const modifiedDataset = {
    currentLanguage: window.document.documentElement.lang,
    globalApiUrl: rootElement.dataset.apiUrl,
    ...rootElement.dataset
  };
  const clientAppConfig = parseClientAppConfig(JSON.stringify(modifiedDataset), ['tenant', 'staticApiUrl', 'currentLanguage', 'globalApiUrl']);
  const {
    customerId,
    identityUrl,
    tenant,
    currentLanguage,
    staticApiUrl
  } = clientAppConfig;

  // Initialize Application Insights in production
  if (process.env.NODE_ENV === 'production') {
    ApplicationInsights.initialize(getClientApplicationInsightsConfig());
  } else {
    // when using Requestly we do not want to load token
    // eslint-disable-next-line no-lonely-if -- thats okay
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      // eslint-disable-next-line no-console -- wanted behavior
      console.log('USING REQUESTLY');
    } else {
      if (!identityUrl) {
        throw new Error('Missing Identity URL!');
      }
      await doDevelopmentLogin({
        identityUrl,
        tenant
      });
    }
  }
  const environmentProviderProps = {
    tenant
  };
  const serviceUrlsProviderProps = {
    serviceUrls: clientAppConfig
  };
  const universalTokenProviderProps = {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- could happen when CAP config is not correctly provided in which case the app should be initialized anyway
    customerId: customerId ?? NOT_LOGGED_IN_USER_ID,
    initialToken: ''
  };
  const testIdProviderProps = {
    team: Team.AcquisitionGrowth,
    project: APP_NAME
  };
  const translationsProviderProps = {
    currentLanguage,
    defaultTranslationsWithNamespaces,
    clientFetchConfiguration: {
      baseUrl: staticApiUrl,
      onError: cause => logger.error(new Error('Error occured while loading translations', {
        cause
      })),
      tenant
    }
  };

  // Only show for authorized users
  if (universalTokenProviderProps.customerId === NOT_LOGGED_IN_USER_ID) {
    logger.info('Preventing showing notification-menu CAP content for unauthorized user');
    return;
  }
  let content = /*#__PURE__*/_jsx(LoggerProvider, {
    logger: mainLogger,
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      areaName: "appRender",
      children: /*#__PURE__*/_jsx(EnvironmentProvider, {
        ...environmentProviderProps,
        children: /*#__PURE__*/_jsx(ServiceUrlsProvider, {
          ...serviceUrlsProviderProps,
          children: /*#__PURE__*/_jsx(UniversalTokenProvider, {
            ...universalTokenProviderProps,
            children: /*#__PURE__*/_jsx(TestIdProvider, {
              ...testIdProviderProps,
              children: /*#__PURE__*/_jsx(TranslationsProvider, {
                ...translationsProviderProps,
                children: /*#__PURE__*/_jsx(NotificationsMenu, {})
              })
            })
          })
        })
      })
    })
  });
  if (process.env.NODE_ENV === 'development') {
    content = /*#__PURE__*/_jsx(DevelopmentEnvironmentWrapper, {
      children: content
    });
  }
  createRoot(rootElement).render(/*#__PURE__*/_jsx(EmotionCacheProvider, {
    cacheKey: EMOTION_CACHE_KEY,
    children: /*#__PURE__*/_jsx(ThemeProvider, {
      children: content
    })
  }));
}